<template>
  <div
    :class="[`item item-${variant}`, { selected, '_pointer-events-none': disabled && !readonly }]"
    @click.left="$emit('click', getEventPayload($event, item.id))"
    @contextmenu.exact.stop.prevent="$emit('contextmenu', $event)"
  >
    <slot name="top" />
    <div class="flex items-start gap-1">
      <div class="flex-none text-sm font-light min-w-4 mr-1">
        {{ 'order' in item ? Number(item.order) + 1 : item.camera_order + 1 }}
      </div>
      <div class="flex flex-col flex-grow gap-1 items-start min-w-0 relative">
        <div class="thumbnail"><slot /></div>
        <div class="flex items-center max-w-full">
          <slot name="icon" />
          <div v-if="!!readonly" class="dark py-0 px-0 max-w-full truncate">{{ editTitle }}</div>
          <InlineEditable
            v-else
            :value="editTitle"
            class="py-0 px-0 min-w-0 max-w-full / focus-within_px-2 hover_px-2 focus-within_whitespace-normal focus-within_break-words focus-within_bg-black"
            data-intercom-target="slide-list-title"
            theme="dark"
            :disabled="!editable || disabled"
            :maxlength="255"
            :placeholder="placeholder"
            :focus-on-mount="focusOnMount"
            @click.stop=""
            @focus="$emit('focus:title')"
            @blur="$emit('blur:title')"
            @save="$emit('update:title', editTitle)"
            @clear="resetEditTitle"
            @cancel="resetEditTitle"
            @input="input"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { DeepReadonly, PropType } from 'vue'
import type { CameraData } from '@/entities/model3d'
import type { Slide } from '@/entities/deck'

const props = defineProps({
  item: {
    type: Object as PropType<DeepReadonly<Slide> | CameraData>,
    required: true,
  },
  variant: {
    type: String as PropType<'list' | 'search'>,
    default: 'list',
  },
  selected: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: '',
  },
  editable: {
    type: Boolean,
    default: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  focusOnMount: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['click', 'update:title', 'contextmenu', 'focus:title', 'blur:title'])

const editTitle = ref(props.focusOnMount ? '' : props.item.title)

watch(
  () => props.item.title,
  () => (editTitle.value = props.item.title)
)

function getEventPayload(e: MouseEvent, payload: number): MouseEvent & { payload: number } {
  return Object.assign(e, { payload })
}

function resetEditTitle(): void {
  editTitle.value = props.item.title
  emit('update:title', props.item.title)
}

function input(text: string): void {
  editTitle.value = text
}
</script>

<style lang="stylus" scoped>
.selected
  @apply bg-copper-200 bg-opacity-20 border-copper-200

.item
  @apply border-transparent overflow-hidden rounded-md appearance-none p-1 bg-transparent w-full outline-none border whitespace-nowrap overflow-ellipsis transition-all

  .thumbnail
    @apply min-w-0 rounded-lg relative overflow-hidden w-full
    aspect-ratio 16 / 9

  &-list
    @apply hover_bg-gray-900
    &.selected
      @extends .selected
    .thumbnail
      @apply bg-gray-700
      &:hover
        outline 1px solid theme('colors.gray.800')

  &-search
    @apply p-3 pt-1 text-gray-800 text-sm font-medium hover_bg-gray-700 hover_bg-opacity-20
    &.selected
      @extends .selected
      @apply text-copper-800 font-semibold border-copper-700

    .thumbnail
      @apply bg-gray-700
</style>
